import * as React from "react";
import type { Category } from "../../models/Category";
import { useUser } from "../UserContext";
import { createApiClient } from "../useApiClient";
import { useSafeRequest } from "../useSafeRequest";
import { useReferenceData } from "../ReferenceDataContext";
import { useIsAuthenticated } from "@azure/msal-react";
import { buildTree, flattenApiResponse, type ApiCategoryNode, type CategoryNode } from "./categoryTree.utils";
import { getApiErrorMessage } from "./apiError.utils";

export type CategoryInput = {
    name: string;
    parentId: number | null;
};

export type SaveCategoryRequest = {
    id?: number;
    name: string;
    clientId: number | null; // null = system, else scoped to the active client
    parentId: number | null;
};

export function useCategoriesAdmin() {
    const { activeClientId, getAccessToken } = useUser();
    const { refresh: refreshReferenceData } = useReferenceData();
    const isAuthenticated = useIsAuthenticated();

    const clientIdRef = React.useRef<number | null>(null);
    React.useEffect(() => {
        clientIdRef.current = activeClientId;
    }, [activeClientId]);

    const api = React.useMemo(
        () => createApiClient({ getAccessToken, getClientId: () => clientIdRef.current }, { timeoutMs: 12000 }),
        [getAccessToken]
    );
    // Typed to accept nested `children`, since the `format=tree` request
    // below may come back as an actual nested tree rather than flat rows —
    // see flattenApiResponse in categoryTree.utils.ts.
    const listRequest = useSafeRequest<ApiCategoryNode[]>(api, { maxAttempts: 3 });

    const [includeSystem, setIncludeSystem] = React.useState(true);
    const [categories, setCategories] = React.useState<Category[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState("");
    const [status, setStatus] = React.useState("");

    const reload = React.useCallback(async () => {
        const url = `/categories?includeSystem=${includeSystem}&format=tree`;
        const data = await listRequest.run({ method: "GET", url });
        setCategories(flattenApiResponse(data));
    }, [includeSystem, listRequest]);

    // Route the initial/refresh load through a ref updated on every render
    // rather than depending on `reload` directly — `reload` depends on
    // `listRequest`, which comes from useSafeRequest(api, ...) and may not
    // be referentially stable across renders. Depending on it directly can
    // set up an infinite fetch loop (state update -> re-render -> new
    // `reload` identity -> effect re-fires -> ...).
    const reloadRef = React.useRef(reload);
    reloadRef.current = reload;

    React.useEffect(() => {
        if (!isAuthenticated || activeClientId == null) return;
        let cancelled = false;

        (async () => {
            setLoading(true);
            setError("");
            try {
                await reloadRef.current();
            } catch (e: any) {
                if (!cancelled) setError(getApiErrorMessage(e, "Unable to load categories"));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, activeClientId, includeSystem]);

    const isSystemCategory = React.useCallback(
        (categoryId: number) => categories.find((c) => c.id === categoryId)?.clientId == null,
        [categories]
    );

    const createCategory = React.useCallback(
        async (input: CategoryInput) => {
            const body: SaveCategoryRequest = {
                name: input.name.trim(),
                clientId: activeClientId,
                parentId: input.parentId,
            };
            const response = await api.post<Category>("/categories", body);
            const created = response.data;
            await reload();
            setStatus(`"${created.name}" created`);
            refreshReferenceData?.();
            return created;
        },
        [activeClientId, api, reload, refreshReferenceData]
    );

    const updateCategory = React.useCallback(
        async (categoryId: number, input: CategoryInput) => {
            if (isSystemCategory(categoryId)) {
                throw new Error("System categories can't be edited from here.");
            }
            const body: SaveCategoryRequest = {
                id: categoryId,
                name: input.name.trim(),
                clientId: activeClientId,
                parentId: input.parentId,
            };
            await api.put(`/categories/${categoryId}`, body);
            await reload();
            setStatus(`"${input.name.trim()}" updated`);
            refreshReferenceData?.();
        },
        [activeClientId, api, isSystemCategory, reload, refreshReferenceData]
    );

    /** Reparent a single node via drag-and-drop without opening the form. */
    const moveCategory = React.useCallback(
        async (categoryId: number, newParentId: number | null) => {
            const current = categories.find((c) => c.id === categoryId);
            if (!current) return;
            try {
                await updateCategory(categoryId, { name: current.name, parentId: newParentId });
            } catch (e: any) {
                setError(getApiErrorMessage(e, `Couldn't move "${current.name}"`));
            }
        },
        [categories, updateCategory]
    );

    const deleteCategory = React.useCallback(
        async (categoryId: number) => {
            if (isSystemCategory(categoryId)) {
                throw new Error("System categories can't be removed from here.");
            }
            await api.delete(`/categories/${categoryId}`);
            await reload();
            setStatus("Category removed");
            refreshReferenceData?.();
        },
        [api, isSystemCategory, reload, refreshReferenceData]
    );

    const tree = React.useMemo<CategoryNode[]>(() => buildTree(categories), [categories]);

    return {
        loading,
        error,
        clearError: () => setError(""),
        status,
        clearStatus: () => setStatus(""),
        includeSystem,
        setIncludeSystem,
        categories,
        tree,
        reload,
        isSystemCategory,
        createCategory,
        updateCategory,
        moveCategory,
        deleteCategory,
    };
}
